import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Loader2, Plus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CreateTeamDialog } from "@/components/teams/CreateTeamDialog";
import { PendingInvitationsCard } from "@/components/teams/PendingInvitationsCard";
import { useTeams } from "@/lib/api/teams.queries";

export const Route = createFileRoute("/teams/")({
  component: TeamsPage,
});

function TeamsPage() {
  return (
    <RequireAuth>
      <AppShell>
        <TeamsContent />
      </AppShell>
    </RequireAuth>
  );
}

const ROLE_VARIANT: Record<string, "default" | "muted" | "secondary"> = {
  owner: "default",
  admin: "default",
  accountant: "secondary",
  cashier: "secondary",
  viewer: "muted",
};

function TeamsContent() {
  const { t } = useTranslation();
  const teamsQuery = useTeams();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("teams.page.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("teams.page.subtitle")}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          {t("teams.page.create_cta")}
        </Button>
      </div>

      <PendingInvitationsCard />

      {teamsQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : teamsQuery.isError ? (
        <ErrorState
          title={t("teams.page.error.title")}
          description={t("teams.page.error.body")}
          onRetry={() => void teamsQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : (teamsQuery.data ?? []).length === 0 ? (
        <EmptyTeamsState onCreate={() => setCreateOpen(true)} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {(teamsQuery.data ?? []).map((team) => {
            const variant = ROLE_VARIANT[team.role] ?? "muted";
            return (
              <Card key={team.id} className="transition-shadow hover:shadow-md">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="truncate">{team.name}</CardTitle>
                      <CardDescription className="font-mono text-xs">
                        {team.slug}
                      </CardDescription>
                    </div>
                    <Badge variant={variant}>
                      {t(`teams.role.${team.role}`)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <Link
                    to="/teams/$teamId"
                    params={{ teamId: team.id }}
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  >
                    {t("teams.page.detail_cta")}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CreateTeamDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function EmptyTeamsState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Users className="h-8 w-8 text-primary" />
        </div>
        <div className="max-w-sm space-y-1">
          <h2 className="text-lg font-semibold">{t("teams.empty.title")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("teams.empty.body")}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button onClick={onCreate}>
            <Plus className="h-4 w-4" />
            {t("teams.empty.create_cta")}
          </Button>
          <Button asChild variant="outline">
            <Link to="/teams/accept">{t("teams.empty.accept_cta")}</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
